import { useState, type ReactNode } from 'react'
import styles from './Accordion.module.css'

export interface AccordionProps {
  /** Header content — string renders as uppercase label, ReactNode renders as-is */
  label: ReactNode
  /** Optional actions rendered between label and chevron (won't trigger toggle) */
  actions?: ReactNode
  defaultOpen?: boolean
  open?: boolean
  /** Called when the accordion is toggled, with the new open state */
  onToggle?: (open: boolean) => void
  compact?: boolean
  /**
   * When the accordion itself is narrow (≤768px wide — a phone, or a drawer on
   * any screen), move `actions` onto their own full-width row under the label
   * instead of squeezing the label beside them. For headers whose actions are
   * wide — several buttons, or text such as a contact.
   */
  stackActionsOnMobile?: boolean
  className?: string
  children: ReactNode
}

export function Accordion({ label, actions, defaultOpen = false, open, onToggle, compact, stackActionsOnMobile, className, children }: AccordionProps) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen)
  const isControlled = open !== undefined
  const isOpen = isControlled ? open : internalOpen

  const toggle = () => {
    if (!isControlled) {
      setInternalOpen(prev => {
        const next = !prev
        onToggle?.(next)
        return next
      })
    } else {
      onToggle?.(!isOpen)
    }
  }

  const isStringLabel = typeof label === 'string'

  const rootClasses = [
    styles.accordion,
    compact && styles.compact,
    stackActionsOnMobile && styles.stackRoot,
    className,
  ].filter(Boolean).join(' ')

  const chevron = (
    <svg
      className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
    >
      <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )

  // The header is a row, not a button: `actions` regularly holds buttons and
  // links, and nesting those inside the trigger is invalid HTML that breaks
  // keyboard and screen-reader semantics. The trigger button covers the label
  // and the free space beside it; the chevron is a redundant pointer affordance
  // (hidden from assistive tech, which uses the trigger).
  return (
    <div className={rootClasses}>
      <div className={[styles.header, isStringLabel ? styles.headerSimple : styles.headerCustom, stackActionsOnMobile && styles.stackActions].filter(Boolean).join(' ')}>
        <button
          type="button"
          className={styles.trigger}
          onClick={toggle}
          aria-expanded={isOpen}
        >
          <span className={styles.label}>{label}</span>
        </button>
        {actions && <span className={styles.actions}>{actions}</span>}
        <span className={styles.chevronHit} onClick={toggle} aria-hidden="true">
          {chevron}
        </span>
      </div>
      <div className={`${styles.content} ${isOpen ? styles.contentOpen : ''}`}>
        <div className={styles.inner}>
          {children}
        </div>
      </div>
    </div>
  )
}
